import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getParents, createParent, updateParent, deleteParent } from '../../apis/parents';
import { RootState } from '../store';

export interface Parent {
    id: string;
    full_name: string;
    email: string;
    phone_number?: string;
    bio?: string;
    profile_picture_url?: string;
    total_credits?: number;
    deleted_at?: string | null;
    lesson_count?: number;
    student_count?: number;
    profile?: any;
}

interface ParentState {
    parents: Parent[];
    total: number;
    page: number;
    size: number;
    pages: number;
    loading: boolean;
    error: string | null;
}

const initialState: ParentState = {
    parents: [],
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    loading: false,
    error: null,
};

// Thunks
export const fetchParents = createAsyncThunk(
    'parents/fetchParents',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getParents(params);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not fetch parents');
            return rejectWithValue(errorText);
        }
    }
);

export const addParent = createAsyncThunk(
    'parents/addParent',
    async (data: Partial<Parent>, { rejectWithValue }) => {
        try {
            const response = await createParent(data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not add parent');
            return rejectWithValue(errorText);
        }
    }
);

export const editParent = createAsyncThunk(
    'parents/editParent',
    async ({ id, data }: { id: string; data: Partial<Parent> }, { rejectWithValue }) => {
        try {
            const response = await updateParent(id, data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not edit parent');
            return rejectWithValue(errorText);
        }
    }
);

export const removeParent = createAsyncThunk(
    'parents/removeParent',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteParent(id);
            return id;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not delete parent');
            return rejectWithValue(errorText);
        }
    }
);

const parentSlice = createSlice({
    name: 'parents',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchParents.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchParents.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.parents = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
            })
            .addCase(fetchParents.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(addParent.fulfilled, (state, action: PayloadAction<Parent>) => {
                state.parents.push(action.payload);
            })
            .addCase(editParent.fulfilled, (state, action: PayloadAction<Parent>) => {
                const index = state.parents.findIndex((t) => t.id === action.payload.id);
                if (index !== -1) state.parents[index] = action.payload;
            })
            .addCase(removeParent.fulfilled, (state, action: PayloadAction<string>) => {
                state.parents = state.parents.filter((t) => t.id !== action.payload);
            });
    },
});

export const selectAllParents = (state: RootState) => state.parents.parents;
export const selectParentById = (state: RootState, id: string) =>
    state.parents.parents.find((t) => t.id === id);

export default parentSlice.reducer;
